import type { AiSearchEvent, AiSearchResult } from '@/shared/api/graphql/aiSearch';
import { useTranslation } from '@/shared/lib/common/i18n';
import { AiSummaryMarkdownComponents } from '@/shared/ui/components/CareActivities/AiSummaryMarkdownComponents';
import TypewriterAnimation from '@/shared/ui/components/TypewriterAnimation';
import { Body1, Body2, Small } from '@/shared/ui/components/Typography';
import CheckIcon from '@mui/icons-material/Check';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import { alpha, Box, Button, Chip, Collapse, Fade, Stack } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import Markdown from 'react-markdown';
import type { AskPatient, AiSearchTurn as Turn } from '../model/aiSearchStore';
import { currentStep, turnError, turnResult, turnStatus, turnSuggestions } from '../model/aiSearchStore';
import AiSearchResults from './AiSearchResults';
import SuggestedQuestions from './SuggestedQuestions';
import ThinkingBubble from './ThinkingBubble';

/** Milliseconds per character: fast enough that a long answer still reads in a few seconds. */
const TYPING_SPEED = 10;

type Props = {
  turn: Turn;
  events: AiSearchEvent[];
  /** Asks a question as a new turn: a retry, a suggested question or one about a picked patient. */
  onAsk: (question: string, patient?: AskPatient) => void;
  /** Type the answer out. Only the newest turn does, so older answers don't retype on re-render. */
  animate?: boolean;
};

/** The patients a name matched: picking one asks the question about them. */
const PatientChoices = ({ result, onAsk }: { result: AiSearchResult; onAsk: Props['onAsk'] }) => {
  const { t } = useTranslation();
  const { patientChoices, followUpQuestion } = result;
  if (!patientChoices?.length || !followUpQuestion) return null;

  return (
    <Stack spacing={0.75}>
      <Small color="text.secondary">{t.aiSearch.answer.pickPatient}</Small>
      <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap' }}>
        {patientChoices.map(({ truentityId, name, birthDate }) => (
          <Chip
            key={truentityId}
            clickable
            variant="outlined"
            color="primary"
            icon={<PersonOutlineIcon fontSize="small" />}
            // Read as a plain date, not a timestamp - otherwise it shifts by a day.
            label={[name, birthDate && t.aiSearch.answer.born(moment(birthDate, 'YYYY-MM-DD').format('MMM DD YYYY'))]
              .filter(Boolean)
              .join(' · ')}
            onClick={() => onAsk(followUpQuestion, { truentityId, name })}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const AiSearchTurn = ({ turn, events, onAsk, animate = false }: Props) => {
  const { t } = useTranslation();
  const [showSteps, setShowSteps] = useState(false);
  const [typed, setTyped] = useState(!animate);

  const status = turnStatus(turn, events);
  const result = turnResult(events);
  const step = currentStep(events);
  const stepLabel = t.aiSearch.steps[step as keyof typeof t.aiSearch.steps] ?? t.aiSearch.steps.fallback;
  const doneSteps = events.filter(event => event.status === 'DONE' && event.step in t.aiSearch.stepsDone);
  const answerText = (result?.messages ?? []).filter(Boolean).join('\n\n');
  const suggestions = turnSuggestions(events);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '75%',
          background: theme => `linear-gradient(135deg, ${theme.palette.accent.light} 0%, ${theme.palette.accent.main} 100%)`,
          color: 'accent.contrastText',
          // Tighter bottom-right corner points the bubble at its sender, like a chat tail.
          borderRadius: '20px 20px 6px 20px',
          boxShadow: theme => `0 4px 14px ${alpha(theme.palette.accent.main, 0.25)}`,
          px: 2.5,
          py: 1.25
        }}
      >
        <Body1 color="inherit">{turn.question}</Body1>
        {turn.patient?.name && (
          <Small color="inherit" sx={{ opacity: 0.85 }}>
            {turn.patient.name}
          </Small>
        )}
      </Box>

      {status === 'running' && <ThinkingBubble label={step ? stepLabel : t.aiSearch.steps.ACCEPTED} />}

      {status === 'failed' && (
        <Stack spacing={1.5}>
          {/* Says why, then offers questions that can be answered instead. */}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Body2 color="error.main">{turnError(turn, events)}</Body2>
            <Chip
              size="small"
              variant="outlined"
              clickable
              label={t.aiSearch.answer.tryAgain}
              onClick={() => onAsk(turn.question, turn.patient)}
            />
          </Stack>
          <SuggestedQuestions label={t.aiSearch.answer.tryAsking} questions={suggestions} onAsk={onAsk} />
        </Stack>
      )}

      {status === 'done' && result && (
        <Fade in timeout={400}>
          <Stack spacing={1.5}>
            {animate ? (
              <TypewriterAnimation text={answerText} speed={TYPING_SPEED} onDone={() => setTyped(true)} />
            ) : (
              <Body1 component="div">
                <Markdown components={AiSummaryMarkdownComponents}>{answerText}</Markdown>
              </Body1>
            )}

            {/* The results and steps follow the answer once it has been typed out. */}
            {typed && result.intent === 'query' && <AiSearchResults result={result} />}
            {typed && result.intent === 'patients' && <PatientChoices result={result} onAsk={onAsk} />}

            {typed && (
              <SuggestedQuestions
                label={result.intent === 'query' ? t.aiSearch.answer.relatedQuestions : t.aiSearch.answer.tryAsking}
                questions={suggestions}
                onAsk={onAsk}
              />
            )}

            {typed && doneSteps.length > 0 && (
              <Box>
                <Button size="small" onClick={() => setShowSteps(shown => !shown)} sx={{ px: 0, textTransform: 'none' }}>
                  {showSteps ? t.aiSearch.answer.hideSteps : t.aiSearch.answer.showSteps}
                </Button>
                <Collapse in={showSteps}>
                  <Stack spacing={0.5}>
                    {doneSteps.map(event => (
                      <Stack key={event.seq} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <CheckIcon fontSize="small" color="success" />
                        <Small color="text.secondary">
                          {t.aiSearch.stepsDone[event.step as keyof typeof t.aiSearch.stepsDone]}
                          {typeof event.detail?.ms === 'number' ? ` · ${(event.detail.ms / 1000).toFixed(1)}s` : ''}
                        </Small>
                      </Stack>
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            )}
          </Stack>
        </Fade>
      )}
    </Stack>
  );
};

export default AiSearchTurn;
